import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  MessageCircle, Store, Copy, Check, Ticket, Calendar, MapPin,
  Briefcase, BedDouble, Newspaper, ChevronRight,
} from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { waLink, formatBRL, isExpired, couponTypeLabel, productContact } from "@/lib/site";
import { SectionShell } from "@/components/site/primitives";
import AutoCarousel from "@/components/site/AutoCarousel";
import { CategoriesSection, BannersSection } from "@/components/site/sections";
import { ReelsSection } from "@/components/site/Reels";
import {
  SECTION_TYPE, DEFAULT_TITLES, DEFAULT_VARIANT, DEFAULT_VARIANT_BY_TYPE, TYPE_LABELS,
} from "@/lib/sectionVariants";

const useList = (resource, params = "") => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!resource) { setItems([]); return; }
    let alive = true;
    api.get(`/public/${resource}${params}`)
      .then((r) => { if (alive) setItems(r.data); })
      .catch((e) => { if (alive) console.error(`Erro ao carregar ${resource}`, e); });
    return () => { alive = false; };
  }, [resource, params]);
  return items;
};

const Img = ({ src, alt, className }) =>
  src ? (
    <img src={fileUrl(src)} alt={alt} className={className} loading="lazy" />
  ) : (
    <div className={`${className} bg-muted flex items-center justify-center`}>
      <Store className="w-8 h-8 text-muted-foreground/40" />
    </div>
  );

const CARO_W = {
  businesses: "w-[270px]", services: "w-[260px]", products: "w-[220px]", coupons: "w-[260px]", promotions: "w-[240px]",
  news: "w-[300px]", events: "w-[320px]", tourist_spots: "w-[280px]",
  jobs: "w-[300px]", rentals: "w-[300px]", commercial_spots: "w-[300px]",
};

const gridClass = (type, variant) => {
  if (variant === "grid2") return "grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4";
  if (variant === "list") return "flex flex-col gap-3";
  if (variant === "grid") {
    if (type === "businesses" || type === "tourist_spots") return "grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4";
    return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4";
  }
  return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4";
};

const Container = ({ type, variant, items, renderCard, section }) => {
  if (variant === "carousel") {
    return (
      <AutoCarousel section={section} testId={`dots-${section.key}`}>
        {items.map((it) => (
          <div key={it.id} className={`snap-item flex-none ${CARO_W[type] || "w-[280px]"}`}>
            {renderCard(it)}
          </div>
        ))}
      </AutoCarousel>
    );
  }
  return <div className={gridClass(type, variant)}>{items.map((it) => <div key={it.id}>{renderCard(it)}</div>)}</div>;
};

/* ---------- Card renderers (no width/snap classes) ---------- */
const BusinessCard = ({ b, layout }) => {
  if (layout === "image_only") {
    return (
      <Link to={`/empresa/${b.id}`} data-testid={`business-card-${b.id}`} className="block bg-card rounded-2xl border border-border shadow-sm overflow-hidden group">
        <Img src={b.cover} alt={b.name} className="w-full h-32 object-cover" />
        <div className="p-3">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-primary">{b.category}</span>
          <h3 className="font-bold text-foreground leading-tight font-heading line-clamp-1 group-hover:text-primary transition-colors">{b.name}</h3>
        </div>
      </Link>
    );
  }
  if (layout === "compact") {
    return (
      <div data-testid={`business-card-${b.id}`} className="bg-card rounded-2xl border border-border shadow-sm p-3 flex items-center gap-3">
        <Img src={b.logo || b.cover} alt="" className="w-12 h-12 rounded-full object-cover shrink-0" />
        <div className="min-w-0 flex-1">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-primary">{b.category}</span>
          <h3 className="font-bold text-foreground leading-tight font-heading line-clamp-1">{b.name}</h3>
        </div>
        <Link to={`/empresa/${b.id}`} data-testid={`business-view-${b.id}`} className="shrink-0 text-xs font-semibold text-primary border border-border rounded-full px-3 py-1.5 hover:border-primary transition-colors">Ver</Link>
      </div>
    );
  }
  return (
    <div data-testid={`business-card-${b.id}`} className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden flex flex-col h-full">
      <div className="relative">
        <Img src={b.cover} alt={b.name} className="w-full h-32 object-cover" />
        <div className="absolute -bottom-6 left-3"><Img src={b.logo} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-white bg-white shadow" /></div>
      </div>
      <div className="pt-8 px-3 pb-3 flex flex-col flex-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-primary">{b.category}</span>
        <h3 className="font-bold text-foreground leading-tight font-heading line-clamp-1">{b.name}</h3>
        <p className="text-xs text-muted-foreground line-clamp-2 mt-1 flex-1">{b.description}</p>
        <div className="flex gap-2 mt-3">
          {b.whatsapp && (
            <a href={waLink(b.whatsapp, `Olá! Encontrei ${b.name} no Divulga Pocinhos Guia.`)} target="_blank" rel="noreferrer" data-testid={`business-wa-${b.id}`} className="flex-1 flex items-center justify-center gap-1 bg-secondary text-secondary-foreground text-xs font-semibold rounded-full py-2 active:scale-95 transition-transform">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
          )}
          <Link to={`/empresa/${b.id}`} data-testid={`business-view-${b.id}`} className="flex-1 flex items-center justify-center gap-1 border border-border text-foreground text-xs font-semibold rounded-full py-2 hover:border-primary transition-colors">Ver empresa</Link>
        </div>
      </div>
    </div>
  );
};

const CouponCard = ({ c, business }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(c.code);
      else {
        const ta = document.createElement("textarea");
        ta.value = c.code; document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
      }
    } catch (e) { /* ignore */ }
    setCopied(true);
    toast.success("Cupom copiado!");
    setTimeout(() => setCopied(false), 2000);
  };
  const wa = c.whatsapp || business?.whatsapp;
  return (
    <div data-testid={`coupon-${c.id}`} className="bg-orange-50 rounded-2xl border border-dashed border-primary/40 p-4 flex flex-col h-full">
      <div className="flex items-center gap-2 mb-2">
        {c.image ? <img src={fileUrl(c.image)} alt="" className="w-9 h-9 rounded-full object-cover border border-white" /> : <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center"><Ticket className="w-4 h-4 text-primary" /></div>}
        <span className="text-xs font-semibold text-muted-foreground line-clamp-1">{business?.name || "Empresa"}</span>
      </div>
      <div className="text-primary font-bold text-lg font-heading leading-tight">{couponTypeLabel(c.type, c.value)}</div>
      <p className="text-sm font-semibold text-foreground mt-0.5 line-clamp-1">{c.title}</p>
      <p className="text-xs text-muted-foreground line-clamp-2 mt-1 flex-1">{c.description}</p>
      <div className="flex items-center justify-between mt-2 bg-white rounded-lg px-3 py-2 border border-dashed border-primary/30">
        <span className="font-mono font-bold text-primary text-sm">{c.code}</span>
        <button onClick={copy} data-testid={`coupon-copy-${c.id}`} className="text-primary hover:opacity-70">{copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}</button>
      </div>
      {wa && (
        <a href={waLink(wa, `Olá! Vi o cupom ${c.code} no Divulga Pocinhos Guia e gostaria de utilizá-lo.`)} target="_blank" rel="noreferrer" data-testid={`coupon-wa-${c.id}`} className="mt-2 flex items-center justify-center gap-2 bg-secondary text-secondary-foreground text-sm font-semibold rounded-full py-2 active:scale-95 transition-transform">
          <MessageCircle className="w-4 h-4" /> Usar pelo WhatsApp
        </a>
      )}
    </div>
  );
};

const PromoCard = ({ p }) => (
  <div data-testid={`promo-${p.id}`} className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden h-full">
    <Img src={p.image} alt={p.title} className="w-full h-32 object-cover" />
    <div className="p-3">
      <h3 className="font-semibold text-sm text-foreground line-clamp-1">{p.title}</h3>
      <p className="text-xs text-muted-foreground line-clamp-2 mt-1 h-8">{p.description}</p>
      <div className="flex items-center gap-2 mt-2">
        {p.old_price ? <span className="text-xs text-muted-foreground line-through">{formatBRL(p.old_price)}</span> : null}
        <span className="text-lg font-bold text-primary font-heading">{formatBRL(p.new_price)}</span>
      </div>
      <Link to={p.business_id ? `/empresa/${p.business_id}` : "#"} className="mt-2 block text-center text-sm font-semibold text-primary border border-primary/30 rounded-full py-1.5 hover:bg-primary hover:text-primary-foreground transition-colors">Ver oferta</Link>
    </div>
  </div>
);

const NewsCard = ({ n, layout }) => {
  if (layout === "large") {
    return (
      <Link to={`/noticia/${n.id}`} data-testid={`news-${n.id}`} className="block bg-card rounded-2xl border border-border shadow-sm overflow-hidden group h-full">
        <Img src={n.image} alt={n.title} className="w-full h-48 sm:h-64 object-cover" />
        <div className="p-4">
          <span className="text-[11px] font-semibold text-primary flex items-center gap-1"><Newspaper className="w-3 h-3" /> {n.category || "Notícia"} · {n.date}</span>
          <h3 className="font-bold text-lg text-foreground leading-tight font-heading line-clamp-2 mt-1 group-hover:text-primary transition-colors">{n.title}</h3>
          <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{n.summary}</p>
        </div>
      </Link>
    );
  }
  const horizontal = layout === "horizontal";
  return (
    <Link to={`/noticia/${n.id}`} data-testid={`news-${n.id}`} className={`bg-card rounded-2xl border border-border shadow-sm overflow-hidden group flex ${horizontal ? "flex-row" : "flex-col sm:flex-row lg:flex-col"} h-full`}>
      <Img src={n.image} alt={n.title} className={horizontal ? "w-28 shrink-0 object-cover" : "w-full sm:w-32 lg:w-full h-40 sm:h-auto lg:h-40 object-cover"} />
      <div className="p-3 flex-1">
        <span className="text-[11px] font-semibold text-primary flex items-center gap-1"><Newspaper className="w-3 h-3" /> {n.category || "Notícia"} · {n.date}</span>
        <h3 className="font-bold text-foreground leading-tight font-heading line-clamp-2 mt-1 group-hover:text-primary transition-colors">{n.title}</h3>
        <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{n.summary}</p>
        <span className="text-sm font-semibold text-primary flex items-center gap-1 mt-2">Ler notícia <ChevronRight className="w-4 h-4" /></span>
      </div>
    </Link>
  );
};

const EventCard = ({ e }) => (
  <Link to={`/evento/${e.id}`} data-testid={`event-${e.id}`} className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden flex group h-full">
    <Img src={e.image} alt={e.name} className="w-28 object-cover" />
    <div className="p-3 flex-1">
      <h3 className="font-bold text-foreground leading-tight font-heading line-clamp-1 group-hover:text-primary transition-colors">{e.name}</h3>
      <div className="text-xs text-muted-foreground mt-1 space-y-1">
        <div className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {e.date} {e.time && `· ${e.time}`}</div>
        <div className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {e.location}</div>
      </div>
      <span className="text-sm font-semibold text-primary mt-2 inline-block">Ver evento</span>
    </div>
  </Link>
);

const TouristCard = ({ t }) => (
  <Link to={`/ponto-turistico/${t.id}`} data-testid={`tourist-${t.id}`} className="block relative rounded-2xl overflow-hidden h-52 group">
    <Img src={t.cover} alt={t.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
    <div className="absolute bottom-0 left-0 p-4 text-white">
      <h3 className="font-bold text-lg font-heading">{t.name}</h3>
      <p className="text-xs text-white/80 line-clamp-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> {t.location}</p>
      <span className="inline-block mt-1 text-xs font-semibold underline">Ver detalhes</span>
    </div>
  </Link>
);

const JobCard = ({ j }) => (
  <div data-testid={`job-${j.id}`} className="bg-card rounded-2xl border border-border shadow-sm p-4 h-full flex flex-col">
    <div className="flex items-center gap-2">
      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center"><Briefcase className="w-5 h-5 text-primary" /></div>
      <div className="min-w-0">
        <h3 className="font-bold text-foreground leading-tight font-heading line-clamp-1">{j.title}</h3>
        <p className="text-xs text-muted-foreground line-clamp-1">{j.company} · {j.location}</p>
      </div>
    </div>
    <p className="text-xs text-muted-foreground line-clamp-2 mt-2 flex-1">{j.description}</p>
    {j.requirements && <p className="text-xs text-foreground mt-1 line-clamp-1"><b>Requisitos:</b> {j.requirements}</p>}
    <Link to={`/vaga/${j.id}`} data-testid={`job-view-${j.id}`} className="mt-3 flex items-center justify-center gap-2 bg-secondary text-secondary-foreground text-sm font-semibold rounded-full py-2 active:scale-95 transition-transform">Ver vaga</Link>
  </div>
);

export const ServiceCard = ({ s, layout }) => {
  const waText = s.wa_button_text || "Falar pelo WhatsApp";
  if (layout === "compact") {
    return (
      <div data-testid={`service-${s.id}`} className="bg-card rounded-2xl border border-border shadow-sm p-3 flex items-center gap-3">
        <Img src={s.image} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0" />
        <div className="min-w-0 flex-1">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-primary">{s.category}</span>
          <h3 className="font-bold text-foreground leading-tight font-heading line-clamp-1">{s.name}</h3>
          <p className="text-xs text-muted-foreground line-clamp-1">{s.professional}</p>
        </div>
        <Link to={`/servico/${s.id}`} data-testid={`service-view-${s.id}`} className="shrink-0 text-xs font-semibold text-primary border border-border rounded-full px-3 py-1.5 hover:border-primary transition-colors">Ver</Link>
      </div>
    );
  }
  return (
    <div data-testid={`service-${s.id}`} className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden flex flex-col h-full">
      <Img src={s.image} alt={s.name} className="w-full h-36 object-cover" />
      <div className="p-3 flex flex-col flex-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-primary">{s.category}</span>
        <h3 className="font-bold text-foreground leading-tight font-heading line-clamp-1">{s.name}</h3>
        {s.professional && <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{s.professional}</p>}
        <p className="text-xs text-muted-foreground line-clamp-2 mt-1 flex-1">{s.description}</p>
        <div className="flex flex-col gap-2 mt-3">
          <Link to={`/servico/${s.id}`} data-testid={`service-view-${s.id}`} className="flex items-center justify-center gap-1 border border-border text-foreground text-xs font-semibold rounded-full py-2 hover:border-primary transition-colors">Ver detalhes</Link>
          {s.whatsapp && (
            <a href={waLink(s.whatsapp, `Olá! Vi o serviço ${s.name} no Divulga Pocinhos Guia.`)} target="_blank" rel="noreferrer" data-testid={`service-wa-${s.id}`} className="flex items-center justify-center gap-1 bg-secondary text-secondary-foreground text-xs font-semibold rounded-full py-2 active:scale-95 transition-transform">
              <MessageCircle className="w-4 h-4" /> {waText}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const ProductCard = ({ p }) => {
  const contact = productContact(p, null);
  const showBtn = p.available !== false && p.contact_enabled !== false && !!contact;
  const btnText = p.contact_button_text || "Ver produto";
  return (
    <div data-testid={`home-product-${p.id}`} className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden flex flex-col h-full">
      <Link to={`/produto/${p.id}`} data-testid={`home-product-open-${p.id}`} className="block">
        <Img src={p.image} alt={p.name} className="w-full h-36 object-cover" />
      </Link>
      <div className="p-3 flex flex-col flex-1">
        <Link to={`/produto/${p.id}`} className="min-w-0">
          <h3 className="font-bold text-foreground leading-tight font-heading line-clamp-1 hover:text-primary transition-colors">{p.name}</h3>
        </Link>
        <p className="text-xs text-muted-foreground line-clamp-2 mt-1 flex-1">{p.description}</p>
        <div className="text-lg font-bold text-primary font-heading mt-2">{formatBRL(p.price)}</div>
        {showBtn ? (
          <a href={contact} target="_blank" rel="noreferrer" data-testid={`home-product-wa-${p.id}`} className="mt-2 flex items-center justify-center gap-2 bg-secondary text-secondary-foreground text-sm font-semibold rounded-full py-2 active:scale-95 transition-transform">
            <MessageCircle className="w-4 h-4" /> {btnText}
          </a>
        ) : (
          <Link to={`/produto/${p.id}`} className="mt-2 flex items-center justify-center gap-1 border border-border text-foreground text-sm font-semibold rounded-full py-2 hover:border-primary transition-colors">Ver produto</Link>
        )}
      </div>
    </div>
  );
};

const PropertyCard = ({ p, prefix }) => (
  <div data-testid={`${prefix}-${p.id}`} className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden flex flex-col h-full">
    <Img src={p.photos && p.photos[0]} alt={p.title} className="w-full h-40 object-cover" />
    <div className="p-3 flex-1 flex flex-col">
      <h3 className="font-bold text-foreground leading-tight font-heading line-clamp-1">{p.title}</h3>
      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> {p.location}</p>
      <p className="text-xs text-muted-foreground line-clamp-2 mt-1 flex-1">{p.description}</p>
      <div className="flex items-center gap-3 mt-2 text-xs text-foreground">
        {p.bedrooms ? <span className="flex items-center gap-1"><BedDouble className="w-3.5 h-3.5" /> {p.bedrooms} quartos</span> : null}
        {p.size ? <span>{p.size}</span> : null}
      </div>
      <div className="text-lg font-bold text-primary font-heading mt-1">{formatBRL(p.price)}<span className="text-xs font-normal text-muted-foreground">/mês</span></div>
      {p.whatsapp && (
        <a href={waLink(p.whatsapp, `Olá! Vi o anúncio "${p.title}" no Divulga Pocinhos Guia e gostaria de mais informações.`)} target="_blank" rel="noreferrer" data-testid={`${prefix}-wa-${p.id}`} className="mt-3 flex items-center justify-center gap-2 bg-secondary text-secondary-foreground text-sm font-semibold rounded-full py-2 active:scale-95 transition-transform">
          <MessageCircle className="w-4 h-4" /> {p.contact || "Contato"}
        </a>
      )}
    </div>
  </div>
);

/* ---------- Generic content section ---------- */
const ContentSection = ({ section }) => {
  const type = section.type || SECTION_TYPE[section.key] || "businesses";
  const title = section.title || DEFAULT_TITLES[section.key] || TYPE_LABELS[type] || "Seção";
  const variant = section.variant || DEFAULT_VARIANT[section.key] || DEFAULT_VARIANT_BY_TYPE[type] || "grid";
  const id = section.key;

  const params = (section.key === "featured_businesses" || section.key === "featured_products") ? "?featured=true" : "";
  let items = useList(type, params);

  // coupons need the business map for names/whatsapp
  const wantBiz = type === "coupons";
  const bizList = useList(wantBiz ? "businesses" : "", "");
  const bizMap = {};
  if (wantBiz) bizList.forEach((b) => (bizMap[b.id] = b));

  if (type === "promotions" || type === "coupons") {
    items = items.filter((x) => !isExpired(x.valid_until));
  }
  if (!items.length) return null;

  const viewAllTo = type === "businesses" ? "/empresas" : undefined;
  const limited = section.key === "all_businesses" && (variant === "grid" || variant === "grid2" || variant === "list")
    ? items.slice(0, 8)
    : items;

  const renderCard = (it) => {
    switch (type) {
      case "businesses": return <BusinessCard b={it} layout={variant === "image_only" ? "image_only" : variant === "compact" ? "compact" : "default"} />;
      case "services": return <ServiceCard s={it} layout={variant === "compact" ? "compact" : "default"} />;
      case "products": return <ProductCard p={it} />;
      case "coupons": return <CouponCard c={it} business={bizMap[it.business_id]} />;
      case "promotions": return <PromoCard p={it} />;
      case "news": return <NewsCard n={it} layout={variant === "horizontal" ? "horizontal" : "default"} />;
      case "events": return <EventCard e={it} />;
      case "tourist_spots": return <TouristCard t={it} />;
      case "jobs": return <JobCard j={it} />;
      case "rentals": return <PropertyCard p={it} prefix="rental" />;
      case "commercial_spots": return <PropertyCard p={it} prefix="commercial" />;
      default: return null;
    }
  };

  // News "featured" layout: one big + list of smaller
  if (type === "news" && variant === "featured") {
    const [first, ...rest] = items;
    return (
      <SectionShell title={title} id={id}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
          <NewsCard n={first} layout="large" />
          <div className="flex flex-col gap-3">
            {rest.slice(0, 4).map((n) => <NewsCard key={n.id} n={n} layout="horizontal" />)}
          </div>
        </div>
      </SectionShell>
    );
  }

  const effectiveVariant = variant === "image_only" || variant === "compact" ? "grid" : variant === "horizontal" ? "grid2" : variant;

  return (
    <SectionShell title={title} id={id} viewAllTo={viewAllTo}>
      <Container type={type} variant={effectiveVariant} items={limited} renderCard={renderCard} section={section} />
    </SectionShell>
  );
};

/* ---------- Public dispatcher used by Home ---------- */
export const HomeSection = ({ section }) => {
  const type = section.type || SECTION_TYPE[section.key];
  if (type === "categories") return <CategoriesSection title={section.title || "Categorias"} section={section} />;
  if (type === "banners") return <BannersSection section={section} />;
  if (type === "reels") return <ReelsSection section={section} />;
  return <ContentSection section={section} />;
};

export default HomeSection;
